// src/components/elements/KanbanBoard/TaskCustomFields/TaskCustomFields.tsx
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Typography, Spin, message } from 'antd';
import CustomFieldValue from './CustomFieldValue';
import type { KanbanTask } from '../../../../stores/KanbanTaskStores';
import type { TaskStore } from '../../../../stores/kanban/TaskStore';
import type { Employee } from '../../../../models/Employee';

const { Title } = Typography;

export type TaskCustomFieldsProps = {
  task: KanbanTask;
  /** The board's store (authenticated or public) — the section reads and writes through it only. */
  store: TaskStore;
  /** Options for user-typed fields; the page passes `employeesStore.users`, the public board `[]`. */
  users: Employee[];
  readOnly: boolean;
  t: (key: string) => string;
};

const TaskCustomFields: React.FC<TaskCustomFieldsProps> = observer(({ task, store, users, readOnly, t }) => {
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        // Field definitions are per project and survive task switches; values are per task.
        if (store.projectCustomFields.length === 0) {
          await store.getProjectCustomFields(String(task.projectId));
        }
        await store.getTaskCustomFieldValues(String(task.id));
      } catch (error) {
        console.error('Error loading custom fields:', error);
        message.error(t('FailedToLoadCustomFields'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadData();
    return () => {
      cancelled = true;
    };
  }, [task.id, task.projectId, store, t]);

  const handleSaveFieldValue = async (fieldId: string, value: string | null): Promise<void> => {
    try {
      const success = await store.setTaskCustomFieldValue(String(task.id), fieldId, value || '');
      if (success) {
        message.success(t('CustomFieldUpdated'));
      } else {
        message.error(t('FailedToUpdateCustomField'));
      }
    } catch (error) {
      console.error('Error saving custom field value:', error);
      message.error(t('ErrorSavingCustomField'));
    }
  };

  if (loading) {
    return (
      <div
        className="task-custom-fields-loading"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}
      >
        <Spin size="small" />
      </div>
    );
  }

  const fields = store.projectCustomFields || [];

  if (fields.length === 0) {
    return <></>;
  }

  const sortedFields = [...fields].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="task-detail-field custom-fields-section">
      <div className="field-header">
        <Title level={5} className="field-title">{t('CustomFields')}</Title>
      </div>

      <Spin spinning={store.isCustomFieldsLoading}>
        <div className="custom-fields-list">
          {sortedFields.map((field) => {
            const fieldValue = store.getFieldValueForTask(String(task.id), field.id);

            return (
              <div key={field.id} className="custom-field-item">
                <div className="custom-field-name">
                  {field.name}
                  {field.required && <span className="required-mark">*</span>}
                </div>
                <CustomFieldValue
                  field={field}
                  value={fieldValue}
                  onSave={(value) => handleSaveFieldValue(field.id, value)}
                  users={users}
                  disabled={readOnly}
                />
              </div>
            );
          })}
        </div>
      </Spin>
    </div>
  );
});

export default TaskCustomFields;
